import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { useCallback, useMemo, useState, useRef, cloneElement, useEffect } from "react";
import "react-reflex/styles.css";
import { Box, Divider, Grid, IconButton, Tooltip } from "@mzc-pdc/ui";
import { IconCollapseWindow, IconExpandWindow } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";

const LAYOUT_RESIZE_HISTORY = `LAYOUT_RESIZE_HISTORY`;

export const SplitTwoTierGridRight = ({
    orientation,
    gridLeft,
    gridRight,
    gridStyle,
    gridRightProps,
    gridLeftProps,
    foldActionButtons,
    expandButton,
    expandButtonToggle,
    defaultExpandButtonDisabled,
}) => {
    const { t } = useTranslation();
    const leftPaneRef = useRef(null);

    const [isFolded, setIsFolded] = useState(!!expandButton); //Note: 초기값의 경우 expandButton 사용처(요청)에서는 접힘, 그 외 사용처에서는 펼침 상태여야해서 해당 조건을 사용했습니다.
    const onHandleClickFoldPanel = useCallback(() => {
        setIsFolded((prev) => !prev);
    }, []);

    useEffect(() => {
        // NOTE: expandButton 모드에서는 expandButtonToggle에 따라 패널 상태를 동기화
        // expandButton true일 때만 열리며, false(댓글창 닫힘)의 경우에는 유지
        if (expandButton) {
            setIsFolded(!expandButtonToggle);
        } else if (expandButtonToggle) {
            setIsFolded(false);
        }
    }, [expandButtonToggle, expandButton]);

    const foldedPanel = useMemo(() => {
        return (
            <Grid
                container
                flexDirection={"column"}
                sx={{
                    height: "100%",
                    borderLeft: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Grid container alignItems={"center"} justifyContent={"center"} sx={{ height: "44px" }}>
                    {expandButton ? (
                        <Grid item>{expandButton}</Grid>
                    ) : (
                        <Grid item>
                            <Tooltip hidden={!isFolded} title={t(`common::label::Expand`, "Expand")}>
                                <IconButton
                                    circled
                                    disabled={defaultExpandButtonDisabled}
                                    onClick={onHandleClickFoldPanel}
                                >
                                    <IconExpandWindow size={16} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip hidden={isFolded} title={t(`common::label::Fold`, "Fold")}>
                                <IconButton
                                    circled
                                    disabled={defaultExpandButtonDisabled}
                                    onClick={onHandleClickFoldPanel}
                                >
                                    <IconCollapseWindow size={16} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>
                <Divider sx={{ mt: `-1px`, backgroundColor: theme.palette.greySecondary[100] }} />
                <Grid container flex={1} alignItems={"end"} justifyContent={"center"} py={2.5}>
                    {foldActionButtons && foldActionButtons}
                </Grid>
            </Grid>
        );
    }, [isFolded, foldActionButtons]);

    return (
        <ReflexContainer orientation={orientation} style={{ flex: 1, overflow: `hidden` }} windowResizeAware={true}>
            <ReflexElement
                key={"left-pane"}
                className="left-pane"
                minSize={gridLeftProps?.minSize}
                maxSize={gridLeftProps?.maxSize}
                style={{
                    height: `calc(100% - ${theme.spacing(2.5)})`,
                    overflow: "hidden",
                }}
            >
                <Box
                    id="left-pane"
                    ref={leftPaneRef}
                    sx={{
                        height: "100%",
                        pr: isFolded ? 3 : undefined,
                        overflow: "auto",
                        scrollbarGutter: "stable",
                        paddingRight: theme.spacing(1),
                        ...(gridStyle?.left ?? {}),
                    }}
                >
                    {cloneElement(gridLeft, { ref: leftPaneRef })}
                </Box>
            </ReflexElement>

            {!isFolded && (
                <ReflexSplitter
                    style={{
                        cursor: `col-resize`,
                        minHeight: `100px`,
                        width: 8,
                        position: `relative`,
                        borderLeft: `none`,
                        backgroundColor: `transparent`,
                        borderColor: theme.palette.greySecondary[100],
                    }}
                />
            )}

            <ReflexElement
                key={"right-pane"}
                className="right-pane"
                minSize={isFolded ? 0 : gridLeftProps?.minSize ? undefined : gridRightProps?.minSize ?? 300}
                maxSize={isFolded ? 1 : gridLeftProps?.maxSize ? undefined : gridRightProps?.maxSize ?? 496}
                size={gridRightProps?.size}
                renderOnResize={false}
            >
                <Box hidden={isFolded} sx={{ height: "100%", ...(gridStyle?.right ?? {}) }}>
                    {gridRight}
                </Box>
            </ReflexElement>
            <ReflexElement className="right-pane2" size={48}>
                <Box sx={{ height: "100%" }}>{foldedPanel}</Box>
            </ReflexElement>
        </ReflexContainer>
    );
};
